import { t } from 'i18next';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  OverlayTrigger,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { Pagination } from '../ui/Pagination';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import download_icon from '../../assets/images/download-icon.svg';
import quit_icon from '../../assets/images/quit-icon.svg';
import CsvIcon from '../../assets/images/CsvIcon';
import PdfIconButton from '../../assets/images/PdfIconButton';
import XlsIcon from '../../assets/images/XlsIcon';
import { utils, writeFileXLSX } from 'xlsx';
import latoRegularUrl from '../../assets/fonts/Lato-Regular.ttf';
import latoBoldUrl from '../../assets/fonts/Lato-Bold.ttf';
import axiosApi from '../../axios/axios-api';
import { toast } from 'react-toastify';
import { LogoPetroprix } from '../ui/LogoPetroprix';
import { useDispatch } from 'react-redux';
import { ModalSolicitarFacturas } from '../facturas/ModalSolicitarFacturas';
import { AiFillCaretDown, AiFillCaretUp } from 'react-icons/ai';
import { downloadPdf } from '../../helpers/downloadPdf';
import { ValidateToken } from '../../helpers/validateToken';
import { startLogout } from '../../store/auth';
import mini_loader from '../../assets/images/loading_anim.gif';
import DownloadInvoiceIcon from '../../assets/images/DownloadInvoiceIcon';
import {
  buildRefuellingCsv,
  buildRefuellingExport,
} from './repostajesExport';

const URL_POST_DOWNLOAD_TICKET = 'repostajes/ticket';
const PDF_FONT_NAME = 'Lato';

let pdfFontsPromise;

const getFontAsBase64 = async (fontUrl) => {
  const response = await fetch(fontUrl);

  if (!response.ok) {
    throw new Error(`Unable to load PDF font: ${response.status}`);
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';

  for (let index = 0; index < bytes.length; index += 32768) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 32768));
  }

  return window.btoa(binary);
};

const loadPdfFonts = () => {
  if (!pdfFontsPromise) {
    pdfFontsPromise = Promise.all([
      getFontAsBase64(latoRegularUrl),
      getFontAsBase64(latoBoldUrl),
    ]);
  }

  return pdfFontsPromise;
};

export const TablaRepostajes = ({
  data,
  token,
  orderData,
  ordered,
  setOrdered,
}) => {
  const [downloadTickets, setDownloadTickets] = useState(1);
  const [allFacturas, setAllFacturas] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [modalFacturasShow, setModalFacturasShow] = useState(false);
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const dispatch = useDispatch();

  const nPages = Math.ceil(data.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = data.slice(indexOfFirstRecord, indexOfLastRecord);

  useEffect(() => {
    setOrdered(false);
  }, [ordered]);

  const exportarCSV = () => {
    const csvContent = buildRefuellingCsv(data, t);
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'repostajes.csv');
    document.body.appendChild(link);

    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const exportarPDF = async () => {
    const unit = 'pt';
    const size = 'A4';
    const orientation = 'landscape';

    const marginLeft = 40;
    const doc = new jsPDF(orientation, unit, size);
    const [regularFont, boldFont] = await loadPdfFonts();

    doc.addFileToVFS('Lato-Regular.ttf', regularFont);
    doc.addFont('Lato-Regular.ttf', PDF_FONT_NAME, 'normal');
    doc.addFileToVFS('Lato-Bold.ttf', boldFont);
    doc.addFont('Lato-Bold.ttf', PDF_FONT_NAME, 'bold');
    doc.setFont(PDF_FONT_NAME, 'normal');
    doc.setFontSize(15);

    const title = t('repostajes.export.report-title');
    const { headers, rows } = buildRefuellingExport(data, t);
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleLines = doc.splitTextToSize(
      title,
      pageWidth - marginLeft * 2
    );
    const titleY = 40;
    const tableStartY = titleY + doc.getLineHeight() * titleLines.length + 15;

    const content = {
      startY: tableStartY,
      head: [headers],
      body: rows,
      styles: {
        font: PDF_FONT_NAME,
        fontStyle: 'normal',
        fontSize: 9,
        overflow: 'linebreak',
      },
      headStyles: {
        font: PDF_FONT_NAME,
        fontStyle: 'bold',
      },
    };

    doc.text(titleLines, marginLeft, titleY);
    doc.autoTable(content);
    doc.save('report.pdf');
  };

  const exportarXLSX = () => {
    const { headers, rows } = buildRefuellingExport(data, t);
    const wb = utils.book_new();
    const ws = utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 18 },
      { wch: 10 },
      { wch: 12 },
      { wch: 20 },
      { wch: 45 },
    ];
    utils.book_append_sheet(wb, ws, t('repostajes.h1'));
    writeFileXLSX(wb, 'repostajes.xlsx');
  };

  const changeButtonsStatus = (block) => {
    if (block) {
      const downloadButtonsSpan = document.querySelectorAll(
        '.repostaje-download-ticket-span'
      );
      downloadButtonsSpan.forEach((button) => {
        button.style.pointerEvents = 'none';
      });

      const downloadWrapped = document.querySelectorAll(
        '.repostaje-download-ticket-span-cursor'
      );
      downloadWrapped.forEach((button) => {
        button.style.cursor = 'not-allowed';
      });
    } else {
      const downloadButtonsSpan = document.querySelectorAll(
        '.repostaje-download-ticket-span'
      );
      downloadButtonsSpan.forEach((button) => {
        button.style.pointerEvents = 'auto';
      });

      const downloadWrapped = document.querySelectorAll(
        '.repostaje-download-ticket-span-cursor'
      );
      downloadWrapped.forEach((button) => {
        button.style.cursor = 'pointer';
      });
    }
  };

  const downloadTicket = async (cmpid, e) => {
    e.preventDefault();

    changeButtonsStatus(true);
    e.target.src = mini_loader;

    try {
      if (process.env.REACT_APP_URL_API && ValidateToken()) {
        const url = `${URL_POST_DOWNLOAD_TICKET}/${cmpid}`;
        const response = await axiosApi.post(
          url,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
        if (response?.data?.code === 200) {
          let data = response.data.data.ticket;
          downloadPdf(data, cmpid, 'ticket');
          changeButtonsStatus(false);
          e.target.src = download_icon;
        } else if (response?.data?.code === 404) {
          toast(t('facturas.form.table.download-error'), {
            icon: <LogoPetroprix />,
          });
          changeButtonsStatus(false);
          e.target.src = download_icon;
          console.warn(response.data.message);
        } else {
          toast(t('facturas.form.table.download-error'), {
            icon: <LogoPetroprix />,
          });
          changeButtonsStatus(false);
          e.target.src = download_icon;
          console.warn(response.data.message);
        }
      } else {
        dispatch(startLogout('expired'));
      }
    } catch (error) {
      toast(t('facturas.form.table.download-error'), {
        icon: <LogoPetroprix />,
      });
      changeButtonsStatus(false);
      e.target.src = download_icon;
      console.error(error);
    }
  };

  const changeSelectFacturas = (e) => {
    setDownloadTickets(!downloadTickets);
  };

  const solicitarFacturas = async () => {
    let facturas = [];

    const repostajes = document.querySelectorAll('div.repostaje');
    repostajes.forEach((repostaje) => {
      const checkbox = repostaje.querySelector('input[type=checkbox]');
      if (checkbox.checked) {
        const id = checkbox.value;
        facturas.push(id);
      }
    });

    setFacturasSeleccionadas(facturas);
    setModalFacturasShow(true);
  };

  const selectAllFacturas = (e) => {
    if (e.target.checked) {
      setAllFacturas(true);
      const repostajes = document.querySelectorAll('div.repostaje');
      repostajes.forEach((repostaje) => {
        const checkbox = repostaje.querySelector('input[type=checkbox]');
        checkbox.checked = true;
      });
    } else {
      setAllFacturas(false);
      const repostajes = document.querySelectorAll('div.repostaje');
      repostajes.forEach((repostaje) => {
        const checkbox = repostaje.querySelector('input[type=checkbox]');
        checkbox.checked = false;
      });
    }
  };

  useEffect(() => {
    if (currentPage > nPages) {
      setCurrentPage(1);
    }
  }, [currentRecords]);

  return (
    <>
      <div className='d-flex justify-content-end mb-3'>
        <OverlayTrigger
          key={1}
          placement='top'
          overlay={
            <Tooltip id={'tooltip1'}>{t('repostajes.export.csv')}</Tooltip>
          }
        >
          <button
            className='btn btn-outline-danger export-button export-btn-color'
            onClick={exportarCSV}
          >
            {/* Pasamos "currentColor" para que el CSV icon use el color del botón (cambia con :hover) */}
            <CsvIcon color='currentColor' />
          </button>
        </OverlayTrigger>

        <OverlayTrigger
          key={3}
          placement='top'
          overlay={
            <Tooltip id={'tooltip3'}>{t('repostajes.export.pdf')}</Tooltip>
          }
        >
          <button
            className='btn btn-outline-danger export-button export-btn-color'
            onClick={exportarPDF}
          >
            <PdfIconButton color='currentColor' />
          </button>
        </OverlayTrigger>

        <OverlayTrigger
          key={2}
          placement='top'
          overlay={
            <Tooltip id={'tooltip2'}>{t('repostajes.export.xls')}</Tooltip>
          }
        >
          <button
            className='btn btn-outline-danger export-button export-btn-color'
            onClick={exportarXLSX}
          >
            <XlsIcon color='currentColor' />
          </button>
        </OverlayTrigger>
      </div>
      <Table className='repostajes' borderless={true} hover={false}>
        <thead>
          <tr>
            <th style={{ width: '8%' }}>
              {t('repostajes.table.fecha')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 0)} />
                <AiFillCaretUp onClick={orderData(true, 0)} />
              </span>
            </th>
            <th style={{ width: '9%' }}>
              {t('repostajes.table.matricula')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 1)} />
                <AiFillCaretUp onClick={orderData(true, 1)} />
              </span>
            </th>
            <th className='d-none d-lg-block' style={{ width: '8%' }}>
              {t('repostajes.table.vehiculo')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 2)} />
                <AiFillCaretUp onClick={orderData(true, 2)} />
              </span>
            </th>
            <th style={{ width: '10%' }}>
              {t('repostajes.table.combustible')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 3)} />
                <AiFillCaretUp onClick={orderData(true, 3)} />
              </span>
            </th>
            <th style={{ width: '8%' }}>
              {t('repostajes.table.litros')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 4)} />
                <AiFillCaretUp onClick={orderData(true, 4)} />
              </span>
            </th>
            <th className='d-none d-lg-block' style={{ width: '8%' }}>
              {t('repostajes.table.precio')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 5)} />
                <AiFillCaretUp onClick={orderData(true, 5)} />
              </span>
            </th>
            <th style={{ width: '14%' }}>
              {t('repostajes.table.tipo-pago')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 6)} />
                <AiFillCaretUp onClick={orderData(true, 6)} />
              </span>
            </th>
            <th className='d-none d-lg-block' style={{ width: '16%' }}>
              {t('repostajes.table.gasolinera')}
              <span className='order-table-icons float-end'>
                <AiFillCaretDown onClick={orderData(false, 7)} />
                <AiFillCaretUp onClick={orderData(true, 7)} />
              </span>
            </th>
            <th style={{ width: '12%' }} id='repostajes-download-title'>
              {downloadTickets ? (
                <span
                // onClick={() => {
                //   changeSelectFacturas();
                // }}
                >
                  {t('repostajes.download.ticket')}
                </span>
              ) : (
                <>
                  <Button
                    variant='danger'
                    size='sm'
                    onClick={solicitarFacturas}
                  >
                    {t('repostajes.table.solicitar-factura')}
                  </Button>
                  <Form.Check
                    type='checkbox'
                    className='repostajes-table-select'
                    onClick={(e) => {
                      selectAllFacturas(e);
                    }}
                  />
                  <span
                    onClick={() => {
                      changeSelectFacturas();
                    }}
                  >
                    <img
                      src={quit_icon}
                      alt='Cerrar'
                      className='repostajes-solicitud-x-btn'
                    />
                  </span>
                </>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {currentRecords.map((repostaje) => (
            <tr key={repostaje.id}>
              <td className='repostajes-fecha'>
                <span className='repostajes-fecha-dia'>
                  {repostaje.fecha.split(' ')[0].replaceAll('-', '/')}
                </span>
                <span className='repostajes-fecha-hora'>
                  {repostaje.fecha.split(' ')[1].slice(0, 5)}
                </span>
              </td>
              <td className='repostajes-matricula'>
                {repostaje.matricula !== null ? repostaje.matricula : '-'}
              </td>
              <td className='repostajes-vehiculo d-none d-lg-block'>
                {repostaje.tipo_vehiculo === '0' ? (
                  t('repostajes.filtros.vehiculos.turismo')
                ) : repostaje.tipo_vehiculo === '1' ? (
                  t('repostajes.filtros.vehiculos.moto')
                ) : repostaje.tipo_vehiculo === '2' ? (
                  t('repostajes.filtros.vehiculos.camion')
                ) : (
                  <OverlayTrigger
                    key={repostaje.id}
                    placement='top'
                    overlay={
                      <Tooltip id={'tooltip3'}>
                        {t('repostajes.table.vehiculo.no-registrado')}
                      </Tooltip>
                    }
                  >
                    <span>-</span>
                  </OverlayTrigger>
                )}
              </td>
              <td className='repostajes-combustible'>
                {repostaje.combustible === 'Gasolina' ? (
                  <Alert variant='success'>
                    {t('datos.modal.editar.form.combustible.gasolina')}
                  </Alert>
                ):(
                  <Alert variant='diesel'>
                    {t('datos.modal.editar.form.combustible.diesel')}
                  </Alert>
                )}
              </td>
              <td className='repostajes-libros'>{repostaje.litros} L</td>
              <td className='repostajes-precio d-none d-lg-block'>
                {repostaje.importe_cob} zł
              </td>
              <td className='repostajes-tipo-pago'>
                {repostaje.tipo_pago === 'Tarjeta' ? (
                  <Alert variant='outline-blue'>
                    {t('repostajes.table.pago-tarjeta')}
                  </Alert>
                ) : repostaje.tipo_pago === 'Petroprix' ? (
                  <Alert variant='outline-blue'>
                    {t('repostajes.table.pago-tarjeta')}
                  </Alert>
                ) : repostaje.tipo_pago === 'Efectivo' ? (
                  <Alert variant='outline-success'>
                    {t('repostajes.table.pago-efectivo')}
                  </Alert>
                ) : (
                  <Alert variant='outline-primary'>
                    {t('repostajes.table.pago-bonusprix')}
                  </Alert>
                )}
              </td>
              <td className='repostajes-gasolinera d-none d-lg-block'>
                <span className='repostajes-gasolinera-nombre'>
                  {repostaje.nombre}
                </span>
                <span className='repostajes-gasolinera-direccion'>
                  {repostaje.direccion}, {repostaje.cp}
                </span>
              </td>
              <td className='repostajes-download'>
                {downloadTickets ? (
                  <OverlayTrigger
                    key={repostaje.id}
                    placement='top'
                    overlay={
                      <Tooltip id={'tooltip3'}>
                        {t('repostajes.download.ticket')}
                      </Tooltip>
                    }
                  >
                    <span className='repostaje-download-ticket-span-cursor'>
                      <span
                        onClick={(e) => {
                          downloadTicket(repostaje.id, e);
                        }}
                        className='repostaje-download-ticket-span'
                      >
                        <DownloadInvoiceIcon />
                      </span>
                    </span>
                  </OverlayTrigger>
                ) : allFacturas ? (
                  <Form.Check
                    type='checkbox'
                    className='repostajes-table-select repostaje'
                    key={repostaje.id}
                    value={repostaje.id}
                    defaultChecked={allFacturas}
                  />
                ) : (
                  <Form.Check
                    type='checkbox'
                    className='repostajes-table-select repostaje'
                    key={repostaje.id}
                    value={repostaje.id}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pagination
        nPages={nPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        recordsPerPage={recordsPerPage}
        totalRecords={data.length}
        setRecordsPerPage={setRecordsPerPage}
      />
      <ModalSolicitarFacturas
        show={modalFacturasShow}
        onHide={() => setModalFacturasShow(false)}
        facturas={facturasSeleccionadas}
      />
    </>
  );
};
